import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { format } from "date-fns";
import Select, { GroupBase, OptionsOrGroups } from "react-select";
import { useDebouncedCallback } from "use-debounce";

import HubInputField from "../inputType";
import useFetch from "src/hooks/useFetch";

import { US_STATES } from "src/constants";
import { APIEndpoints } from "src/types/apiTypes";
import { DiagnosisResponseProps, CaseStepOneProps } from "./types";
import { CASE_INDICATION, CASE_SITE_OF_CARE } from "src/constants/addCase";




const CaseStepOne: FC<CaseStepOneProps> = ({ control, isVisible, watch, setValue, getValues }): JSX.Element => {
  const siteOfCare = watch("shipments_attributes.0.ship_to");
  const diagnosisCode = watch("prescription_attributes.diagnosis_code");

  const [diagnosisFullnames, setDiagnosisFullnames] = useState<string[]>([]);



  /** 
   * @SearchDiagnosisBySearchValue search by API for diagnosis
   * @first initialize useFetch() with diagnosis
   * @second search diagnosis by search term 
   */
  const { loading: diagnosisLoader, data: rfcListing, mutate: getDiagnosis, removeErrorAndData: clearDiagnosisData } =
    useFetch<DiagnosisResponseProps>(APIEndpoints.GET_DIAGNOSIS_DROPDOWN);

  /** @second */
  const searchDiagnosis = (search: string) => {
    if (typeof search === "string" && search.length >= 2) {
      clearDiagnosisData();
      getDiagnosis(null, null, { search });
    }
  };

  const debounced = useDebouncedCallback(searchDiagnosis, 500);



  useEffect(() => {
    if (diagnosisCode || (rfcListing && rfcListing.data)) {
      setTimeout(() => clearDiagnosisData(), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosisCode]);

  useEffect(() => {
    if (siteOfCare && siteOfCare !== "Other") {
      setValue("shipments_attributes.0", {
        ship_to_city: "",
        ship_to_state: "",
        ship_to_street: "",
        ship_to_zip: "",
        ship_to: getValues("shipments_attributes.0.ship_to"),
        ship_to_suite: "",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteOfCare]);

  return (
    <div className={`card mb-3 ${!isVisible ? 'd-none' : ''}`}>
      <div className="card-header bg-success-subtle">
        <h4 className="text-black m-0">Getting Started</h4>
      </div>

      <div className="card-body">
        <p>
          In order to process your request for benefit investigation, the following
          information must be completed. All information is kept confidential according
          to HIPAA guidelines. Please fill out the fields below as completely as possible.
          Missing fields may result in a delay to processing patient benefits in a timely manner.
        </p>

        <div className="row">
          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.indication"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  isRowBased={true}
                  isRequired={true}
                  isRowInsideAnother={true}
                  inputId="prescription_attributes.indication"
                  inputLabel="Indication"
                  inputPlaceholder="Select a Value..."
                  field={field}
                  error={error}
                  isTypeAhead
                  isDropdown={true}
                  isSearchable={false}
                  dropdownData={CASE_INDICATION as unknown as OptionsOrGroups<string, GroupBase<string>>}
                />
              )}
            />
          </div>

          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.jcode"
              control={control}
              render={({ field }) => (
                <div className="row mb-3">
                  <label htmlFor="prescription_attributes.jcode" className="form-label col-lg-4 col-md-4 col-12">
                    QUTENZA J-Code *
                  </label>
                  <div className="col-lg-8 col-md-8 col-12">
                    <input disabled type="text" className="form-control" id="prescription_attributes.jcode" {...field} />
                  </div>
                </div>
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <div className="row mb-3">
              <label htmlFor="prescription_attributes.diagnosis_code" className="form-label col-lg-4 col-md-4 col-12">
                Diagnosis Code *
              </label>
            
              <Controller
                name="prescription_attributes.diagnosis_code"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const options =
                    rfcListing?.data && rfcListing?.data?.length > 0
                      ? rfcListing.data.map((opt: any) => ({
                        value: opt.code,
                        label: opt.fullname,
                      }))
                      : [];
                  return (
                    <div className="col-lg-8 col-md-8 col-12">
                      <Select
                        options={options}
                        isMulti
                        isSearchable
                        placeholder="Enter Diagnosis Code"
                        className={error ? "is-invalid" : ""}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: error ? "red" : base.borderColor,
                          }),
                        }}
                        {...field}
                        value={field.value.map((item) => ({ value:item, label:item }))}
                        onChange={(val: any) => {
                          // Update form field with codes
                          field.onChange(val.map((item: any) => item.value));

                          // Update state with fullnames for tooltip
                          const fullnames = val.map((item: any) => item.label);
                          setDiagnosisFullnames((oldVal) => oldVal.concat(fullnames));

                          console.log("Selected codes:", val.map((i: any) => i.value));
                          console.log("Selected fullnames:", fullnames);
                        }}
                        onInputChange={debounced}
                        noOptionsMessage={() =>
                          diagnosisLoader ? "Loading..." : "No diagnosis codes"
                        }
                        formatOptionLabel={(option: any) => (
                          <div
                            data-bs-toggle="tooltip"
                            data-bs-placement="right"
                            title={diagnosisFullnames.find((item)=>item.includes(option.label)) || ""} // show full name in tooltip
                          >
                            {option.label.length > 50
                              ? `${option.label.substring(0, 50)}...`
                              : option.label}
                          </div>
                        )}
                      />

                      {error?.message && (
                        <div className="text-danger text-small mt-2">{error.message}</div>
                      )}
                    </div>
                  );
                }}
              />

            </div>
          </div>

          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.note"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="prescription_attributes.note"
                  isRowBased={true}
                  isRowInsideAnother={true}
                  isRequired={false}
                  field={field}
                  inputLabel="Notes"
                  inputPlaceholder="Note Shared With Patient"
                  error={error}
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <Controller
              name="procedure_attributes.cpt_code"
              control={control}
              // defaultValue={0}
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="procedure_attributes.cpt_code"
                  inputLabel="Procedure Code for Adminstration (CPT)"
                  isRequired={true}
                  isRowBased={true}
                  isRowInsideAnother={true}
                  field={field}
                  error={error}
                  inputType="number"
                />
              )}
            />
          </div>

          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.direction"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="prescription_attributes.direction"
                  isRowBased={true}
                  isRequired={false}
                  isRowInsideAnother={true}
                  field={field}
                  inputLabel="Directions"
                  inputPlaceholder=""
                  error={error}
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.qty"
              control={control}
              // defaultValue={0}
              render={({ field, fieldState: { error } }) => (
                <div className="row justify-content-center mb-3">
                  <label className="form-label col-lg-4 col-md-4 col-12" htmlFor="prescription_attributes.days_supply">Qty*</label>
                  <div className="col-lg-8 col-md-8 col-12">
                    <div className="input-group qty-dropdown">
                      <input type="number" id="prescription_attributes.days_supply"
                        className={`form-control ${error && error.message ? 'is-invalid' : ''}`}
                        aria-describedby="basic-addon2" {...field} />
                      <span className="input-group-text" id="basic-addon2">topical systems</span>
                    </div>
                    {error && error.message ? (
                      <div
                        //dangerouslySetInnerHTML={{__html: error.message}}
                        className="text-danger text-small mt-2 ">Quantity is required</div>
                    ) : null}
                  </div>
                </div>
              )}
            />
          </div>

          <div className="col-lg-6">
            <Controller
              name="shipments_attributes.0.ship_to"
              control={control}
              defaultValue="Provider Office"
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  isRequired={true}
                  isRowBased={true}
                  isRowInsideAnother={true}
                  inputId="shipments_attributes.0.ship_to"
                  inputLabel="Care Site"
                  field={field}
                  error={error}
                  isTypeAhead
                  isDropdown={true}
                  isSearchable={false}
                  dropdownData={CASE_SITE_OF_CARE as unknown as OptionsOrGroups<string, GroupBase<string>>}
                />
              )}
            />
          </div>
        </div>

        {siteOfCare === "Other" ? (
          <>
            <div className="row">
              <div className="col-lg-6">
                <Controller
                  name="shipments_attributes.0.ship_to_street"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <div className={"row"}>
                      <label className={"form-label col-lg-4 col-md-4 col-12"}>
                        Street *
                      </label>
                      <div className={"col-lg-8 col-md-8 col-12"}>
                        <HubInputField
                          isRowBased={true}
                          isRequired={false}
                          inputId="shipments_attributes.0.ship_to_street"
                          inputPlaceholder="Street"
                          field={field}
                          error={error}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>

              <div className="col-lg-6">
                <Controller
                  name="shipments_attributes.0.ship_to_suite"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <div className={"row"}>
                      <label className={"form-label col-lg-4 col-md-4 col-12"}> Suite </label>
                      <div className={"col-lg-8 col-md-8 col-12"}>
                        <HubInputField
                          isRowBased={true}
                          isRequired={false}
                          inputId="shipments_attributes.0.ship_to_suite"
                          inputPlaceholder="Suite"
                          field={field}
                          error={error}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-6">
                <Controller
                  name="shipments_attributes.0.ship_to_city"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <div className={"row"}>
                      <label className={"form-label col-lg-4 col-md-4 col-12"}> City *</label>
                      <div className={"col-lg-8 col-md-8 col-12"}>
                        <HubInputField
                          isRowBased={true}
                          isRequired={false}
                          inputId="shipments_attributes.0.ship_to_city"
                          inputPlaceholder="City"
                          field={field}
                          error={error}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>

              <div className="col-lg-6">
                <Controller
                  name="shipments_attributes.0.ship_to_state"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <div className={"row"}>
                      <label className={"form-label col-lg-4 col-md-4 col-12"}> State *</label>
                      <div className={"col-lg-8 col-md-8 col-12"}>
                        <HubInputField
                          isRowBased={true}
                          isRequired={false}
                          inputId="shipments_attributes.0.ship_to_state"
                          inputPlaceholder="Select A State..."
                          field={field}
                          error={error}
                          isDropdown={true}
                          isSearchable={true}
                          isTypeAhead
                          dropdownData={US_STATES as unknown as OptionsOrGroups<string, GroupBase<string>>}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>

            </div>

            <div className="row">
              <div className="col-lg-6">
                <Controller
                  name="shipments_attributes.0.ship_to_zip"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <div className={"row"}>
                      <label className={"form-label col-lg-4 col-md-4 col-12"}> Zip *</label>
                      <div className={"col-lg-8 col-md-8 col-12"}>
                        <HubInputField
                          isRowBased={true}
                          isRequired={false}
                          inputId="shipments_attributes.0.ship_to_zip"
                          inputPlaceholder="Zip"
                          field={field}
                          error={error}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>
          </>
        ) : null}

        <Controller
          name="prescription_attributes.written_date"
          control={control}
          defaultValue={format(new Date(), "MM/dd/yyyy")}
          render={({ field: { value } }) => (
              <div className="row">
                <div className="col-lg-2 col-md-4 col-12"></div>
                <div className="col-lg-10 col-md-8 col-12">
                  <div className="form-check mt-3">
                    <input className="form-check-input" type="checkbox" id="flexCheckIndeterminateDisabled" disabled checked />
                    <label className="form-check-label" htmlFor="flexCheckIndeterminateDisabled">
                      Dispense as Written (written date will be {value})
                    </label>
                  </div>
                </div>
              </div>
          )}
        />

      </div>
    </div>
  );
};

export default CaseStepOne;
